import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useGetToken } from '~/auth/useGetToken'
import { config, getLiveOnly, setLiveOnly as persistLiveOnly, setStoredToken } from '~/config'
import { embedded } from '~/embed'
import { createClient } from '~/data/graphql'
import {
  fetchAgentAccountsAll,
  fetchAgentActivities,
  fetchAgentLooks,
  mergeAgentLooks,
  type AgentLooksSource,
  fetchAgentBrief,
  fetchAgentRequests,
  fetchOfficeHealth,
  fetchOffices,
  fetchRoomTasks,
  buildMessageDestinations,
  deliverMessage,
  pingPerson,
  type MessageDestination,
  type DeliveryNotify,
  type RoomTask,
} from '~/data/interact'
import { parseApiTime } from '~/data/meetings'
import { fetchMeetings } from '~/data/queries'
import { buildSnapshot } from '~/data/snapshot'
import { withDemoMeetings } from '~/dev/demoMeetings'
import type { Person, WorldSnapshot } from '~/data/types'
import { startAmbient } from '~/game/ambient'
import { addRoomOffset, clearRoomOffsets, hasRoomOffsets } from '~/game/arrange'
import { isSfxOn, setSfxOn } from '~/game/sfx'
import { PhaserGame } from '~/game/PhaserGame'
import { AGENTS_ID, computeLayout } from '~/game/world'
import { EventBus } from '~/realtime/bus'
import { subscribeWorld } from '~/realtime/channels'
import { getPusher } from '~/realtime/echo'
import { getHub } from '~/realtime/hub'
import { followAgentLooks } from '~/state/agentLooks'
import { useAppStore } from '~/state/store'
import {
  STYLES,
  getOfficeStyle,
  setOfficeStyle,
  type OfficeStyle,
} from '~/game/environment'
import { theme } from '~/theme'
import { AgentCharacter, showsCharacter } from './AgentAvatar'
import { AgentManage } from './AgentManage'
import { AgentTerminal } from './AgentTerminal'
import { HirePanel } from './HirePanel'
import { PeopleDirectory } from './PeopleDirectory'
import { PersonPeek } from './PersonPeek'
import { ReplayControl } from './Replay'
import { fetchNotes, type NoteLite } from '~/data/notes'
import { Loading } from './Loading'
import { NoteDetailPanel } from './NoteDetailPanel'
import { NotesPanel } from './NotesPanel'
import { OfficePanel } from './OfficePanel'
import { RequestsPanel } from './RequestsPanel'
import { RoomPanel } from './RoomPanel'
import { TaskDetailPanel } from './TaskDetailPanel'
import { TasksPanel } from './TasksPanel'
import { WindowFrame } from './WindowFrame'

// Window kinds: cascade slot + width for the floating frames (desktop).
const WIN_DEFS: Record<string, { idx: number; w: number; accent: string }> = {
  person: { idx: 0, w: 308, accent: '#c98a6b' },
  room: { idx: 1, w: 330, accent: '#6f7bd6' },
  tasks: { idx: 2, w: 360, accent: '#e0a13a' },
  notes: { idx: 3, w: 360, accent: '#4a7a5c' },
  office: { idx: 4, w: 340, accent: '#54868c' },
  requests: { idx: 5, w: 340, accent: '#ffd28a' },
  people: { idx: 7, w: 320, accent: '#8fb4c9' },
  hire: { idx: 6, w: 340, accent: '#8a5a8f' },
}

// Layouts persist per org + viewport bucket (FFXIV/EVE lesson: losing your
// arrangement on reload is the #1 windowed-UI complaint; the bucket stops a
// 4K layout stranding windows off a laptop screen).
interface WinStore {
  over: Record<string, { x?: number; y?: number }>
  pinned: string[]
  collapsed: string[]
}
const winStoreKey = (orgId: string): string =>
  `glass-office.windows.${orgId}.${Math.round(window.innerWidth / 200)}x${Math.round(window.innerHeight / 200)}`

function loadWinStore(orgId: string): WinStore {
  const empty: WinStore = { over: {}, pinned: [], collapsed: [] }
  try {
    const raw = localStorage.getItem(winStoreKey(orgId))
    if (!raw) return empty
    const parsed = JSON.parse(raw) as WinStore
    const over: WinStore['over'] = {}
    for (const [key, pos] of Object.entries(parsed.over ?? {})) {
      const def = WIN_DEFS[key]
      if (!def || typeof pos?.x !== 'number' || typeof pos?.y !== 'number') continue
      // Clamp restored rects to the current viewport.
      over[key] = {
        x: Math.min(Math.max(8, pos.x), window.innerWidth - def.w - 8),
        y: Math.min(Math.max(52, pos.y), window.innerHeight - 90),
      }
    }
    return {
      over,
      pinned: (parsed.pinned ?? []).filter((k) => WIN_DEFS[k]),
      collapsed: (parsed.collapsed ?? []).filter((k) => WIN_DEFS[k]),
    }
  } catch {
    return empty
  }
}

/** How many unpinned windows may coexist before the oldest is evicted. */
const WIN_CAP = 4

const WIN_TITLES: Record<string, string> = {
  person: 'Person',
  room: 'Room',
  tasks: 'Tasks',
  notes: 'Notes',
  office: 'Office',
  requests: 'Reviews',
  hire: 'Hire',
  people: 'Directory',
}

export function GameView({ organizationId }: { organizationId: string }) {
  const getToken = useGetToken()
  const queryClient = useQueryClient()
  const setSnapshot = useAppStore((s) => s.setSnapshot)
  const focusUser = useAppStore((s) => s.focusUser)
  const focusedUserId = useAppStore((s) => s.focusedUserId)
  const focusRoom = useAppStore((s) => s.focusRoom)
  const focusedRoomId = useAppStore((s) => s.focusedRoomId)
  const openHire = useAppStore((s) => s.openHire)
  const hireRoomId = useAppStore((s) => s.hireRoomId)
  const reset = useAppStore((s) => s.reset)
  const me = useAppStore((s) => s.me)
  const { logout } = useAuth0()
  const compact = useCompact()
  const touch = useTouch()
  const [liveOnly, setLiveOnly] = useState(getLiveOnly())
  const setMode = (live: boolean) => {
    setLiveOnly(live)
    persistLiveOnly(live)
  }
  const [sound, setSound] = useState(isSfxOn())
  const toggleSound = () => {
    const next = !sound
    setSound(next)
    setSfxOn(next)
  }
  // The agent whose live terminal (work-log tail) is open, if any.
  const [terminalFor, setTerminalFor] = useState<Person | null>(null)
  // The "agents need you" queue: total pending reviews + the panel toggle.
  const [needsYou, setNeedsYou] = useState(0)
  const [requestsOpen, setRequestsOpen] = useState(false)
  // People directory: search + jump-to-person, grouped by room like the floor.
  const [directoryOpen, setDirectoryOpen] = useState(false)
  // Compact screens tuck the HUD controls into a fly-over menu (no wrapping).
  const [menuOpen, setMenuOpen] = useState(false)
  // Desktop ⋯ menu: the occasional controls (replay/sound/switch/sign out).
  const [moreOpen, setMoreOpen] = useState(false)
  // A 4s notice when the window cap silently closes something (else it reads
  // as a bug), and hotkey fallbacks so T/N/O work before any panel was opened.
  const [capToast, setCapToast] = useState<string | null>(null)
  const hotRoomRef = useRef<string | null>(null)
  const officeFallbackRef = useRef<string | null>(null)
  // Clicking a server rack opens that office's in-game health card.
  const [officeFor, setOfficeFor] = useState<string | null>(null)
  // The room work surfaces: task board / notes shelf panels + detail popups.
  const [tasksForId, setTasksForId] = useState<string | null>(null)
  const [notesForId, setNotesForId] = useState<string | null>(null)
  const [taskDetail, setTaskDetail] = useState<{ task: RoomTask; pulseId: string } | null>(null)
  const [noteDetail, setNoteDetail] = useState<{ note: NoteLite; pulseId: string } | null>(null)
  // The window manager: side panels float as draggable windows (desktop).
  // Overrides hold user drags + raises; defaults cascade down the right edge.
  const initialWinStore = useRef<WinStore>()
  // The bucket key is captured ONCE at mount: the persist effect recomputing
  // it live meant a mid-session resize wrote this session's layout into a
  // DIFFERENT bucket's slot, destroying the layout saved for that size.
  const winStoreKeyRef = useRef<string>()
  if (!initialWinStore.current) {
    initialWinStore.current = loadWinStore(organizationId)
    winStoreKeyRef.current = winStoreKey(organizationId)
  }
  const [winOver, setWinOver] = useState<
    Record<string, { x?: number; y?: number; z?: number }>
  >(initialWinStore.current.over)
  const [pinnedWins, setPinnedWins] = useState<string[]>(initialWinStore.current.pinned)
  const [collapsedWins, setCollapsedWins] = useState<string[]>(
    initialWinStore.current.collapsed,
  )
  const topZ = useRef(40)
  const raiseWin = useCallback((key: string) => {
    topZ.current += 1
    const z = topZ.current
    setWinOver((o) => ({ ...o, [key]: { ...o[key], z } }))
  }, [])
  const moveWin = useCallback((key: string, x: number, y: number) => {
    setWinOver((o) => ({ ...o, [key]: { ...o[key], x, y } }))
  }, [])
  const togglePin = useCallback((key: string) => {
    setPinnedWins((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]))
  }, [])
  const toggleCollapse = useCallback((key: string) => {
    setCollapsedWins((c) =>
      c.includes(key) ? c.filter((k) => k !== key) : [...c, key],
    )
  }, [])
  const resetWindows = useCallback(() => {
    setWinOver({})
    setPinnedWins([])
    setCollapsedWins([])
    try {
      localStorage.removeItem(winStoreKeyRef.current ?? winStoreKey(organizationId))
    } catch {
      // best effort
    }
  }, [organizationId])
  // Persist drags/pins/collapses (never z — stacking is per-session).
  useEffect(() => {
    const over: WinStore['over'] = {}
    for (const [k, v] of Object.entries(winOver)) {
      if (typeof v.x === 'number' && typeof v.y === 'number') {
        over[k] = { x: v.x, y: v.y }
      }
    }
    try {
      localStorage.setItem(
        winStoreKeyRef.current ?? winStoreKey(organizationId),
        JSON.stringify({ over, pinned: pinnedWins, collapsed: collapsedWins }),
      )
    } catch {
      // best effort
    }
  }, [winOver, pinnedWins, collapsedWins, organizationId])
  // Viewport shrink re-clamps dragged windows: a window left at x=1500 in a
  // 1920px browser is otherwise stranded fully off-screen at 1280px, with its
  // grip unreachable.
  useEffect(() => {
    const onResize = (): void => {
      setWinOver((o) => {
        let changed = false
        const next: typeof o = {}
        for (const [k, v] of Object.entries(o)) {
          const def = WIN_DEFS[k]
          if (def && typeof v.x === 'number' && typeof v.y === 'number') {
            const x = Math.min(Math.max(8, v.x), window.innerWidth - def.w - 8)
            const y = Math.min(Math.max(52, v.y), window.innerHeight - 90)
            if (x !== v.x || y !== v.y) {
              changed = true
              next[k] = { ...v, x, y }
              continue
            }
          }
          next[k] = v
        }
        return changed ? next : o
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  // compact via ref so the scene callbacks keep a stable identity (they sit
  // in PhaserGame's restart deps).
  const compactRef = useRef(compact)
  useEffect(() => {
    compactRef.current = compact
  }, [compact])
  // Live snapshot of what's open + which window is topmost, for the
  // same-gesture toggle (WoW/Factorio: the gesture that opened it closes it).
  const openSnapRef = useRef<{
    focusedUserId: string | null
    focusedRoomId: string | null
    tasksForId: string | null
    notesForId: string | null
    officeFor: string | null
    topKey: string | null
  }>({
    focusedUserId: null,
    focusedRoomId: null,
    tasksForId: null,
    notesForId: null,
    officeFor: null,
    topKey: null,
  })
  // Last-opened context per kind so hotkeys can reopen (T/N/O).
  const lastOpenedRef = useRef<{ tasks?: string; notes?: string; office?: string }>({})
  // Hover peek: a compact card after 250ms over a character (mouse only).
  const [peek, setPeek] = useState<{ userId: string; x: number; y: number } | null>(null)
  const peekTimer = useRef<number>()
  const onHoverPerson = useCallback((userId: string | null, x: number, y: number) => {
    window.clearTimeout(peekTimer.current)
    if (!userId || compactRef.current) {
      setPeek(null)
      return
    }
    peekTimer.current = window.setTimeout(() => setPeek({ userId, x, y }), 250)
  }, [])
  // Edge event chips (Civ-style): live activity coalesced per room; click =
  // camera jump + the room panel. 30s expiry, newest first, capped.
  const [chips, setChips] = useState<
    Record<string, { count: number; text: string; at: number; name: string }>
  >({})
  // Arrange mode: drag rooms to rearrange the floor; offsets persist per org.
  const [arrangeMode, setArrangeMode] = useState(false)
  const [layoutNonce, setLayoutNonce] = useState(0)
  const [officeStyle, setOfficeStyleState] = useState<OfficeStyle>(() =>
    getOfficeStyle(organizationId),
  )
  const onRoomMoved = useCallback(
    (roomId: string, dx: number, dy: number) => {
      addRoomOffset(organizationId, roomId, dx, dy)
      setLayoutNonce((n) => n + 1)
    },
    [organizationId],
  )
  // Stable identities — these feed PhaserGame's restart deps. On phones a
  // panel replaces the others (single bottom sheet); on desktop windows
  // coexist and the opened one just comes to the front.
  const onOfficeFocus = useCallback(
    (officeId: string) => {
      const s = openSnapRef.current
      if (!compactRef.current && s.officeFor === officeId && s.topKey === 'office') {
        setOfficeFor(null)
        return
      }
      setOfficeFor(officeId)
      lastOpenedRef.current.office = officeId
      if (compactRef.current) {
        focusUser(null)
        focusRoom(null)
        setRequestsOpen(false)
        setTasksForId(null)
        setNotesForId(null)
        setDirectoryOpen(false)
      } else {
        raiseWin('office')
      }
    },
    [focusUser, focusRoom, raiseWin],
  )
  const onTasksFocus = useCallback(
    (pulseId: string) => {
      const s = openSnapRef.current
      if (!compactRef.current && s.tasksForId === pulseId && s.topKey === 'tasks') {
        setTasksForId(null)
        return
      }
      setTasksForId(pulseId)
      lastOpenedRef.current.tasks = pulseId
      if (compactRef.current) {
        setNotesForId(null)
        setOfficeFor(null)
        focusUser(null)
        focusRoom(null)
        setRequestsOpen(false)
        setDirectoryOpen(false)
      } else {
        raiseWin('tasks')
      }
    },
    [focusUser, focusRoom, raiseWin],
  )
  const onNotesFocus = useCallback(
    (pulseId: string) => {
      const s = openSnapRef.current
      if (!compactRef.current && s.notesForId === pulseId && s.topKey === 'notes') {
        setNotesForId(null)
        return
      }
      setNotesForId(pulseId)
      lastOpenedRef.current.notes = pulseId
      if (compactRef.current) {
        setTasksForId(null)
        setOfficeFor(null)
        focusUser(null)
        focusRoom(null)
        setRequestsOpen(false)
        setDirectoryOpen(false)
      } else {
        raiseWin('notes')
      }
    },
    [focusUser, focusRoom, raiseWin],
  )
  // Same-gesture toggle for the world clicks that route through the store.
  const onPersonFocus = useCallback(
    (userId: string | null) => {
      const s = openSnapRef.current
      if (
        userId &&
        !compactRef.current &&
        s.focusedUserId === userId &&
        s.topKey === 'person'
      ) {
        focusUser(null)
        return
      }
      focusUser(userId)
      // Raise in the SAME state batch: the id-change effect alone leaves the
      // default z for one commit, so the WIN_CAP eviction (which sorts by z)
      // would close this just-opened window instead of the oldest — and
      // re-clicking an already-open-but-buried target would never re-raise.
      if (userId) raiseWin('person')
    },
    [focusUser, raiseWin],
  )
  const onRoomFocusToggle = useCallback(
    (pulseId: string | null) => {
      const s = openSnapRef.current
      if (
        pulseId &&
        !compactRef.current &&
        s.focusedRoomId === pulseId &&
        s.topKey === 'room'
      ) {
        focusRoom(null)
        return
      }
      focusRoom(pulseId)
      if (pulseId) raiseWin('room')
    },
    [focusRoom, raiseWin],
  )
  const onHireOpen = useCallback(
    (pulseId: string) => {
      openHire(pulseId)
      raiseWin('hire')
      // Compact renders one sheet in fixed order (hire last) — clear the
      // others or the hire tap looks like a no-op under an open sheet.
      if (compactRef.current) {
        setTasksForId(null)
        setNotesForId(null)
        setOfficeFor(null)
        setRequestsOpen(false)
        setDirectoryOpen(false)
      }
    },
    [openHire, raiseWin],
  )
  const toggleDirectory = useCallback(() => {
    setDirectoryOpen((v) => {
      const next = !v
      if (next) {
        raiseWin('people')
        if (compactRef.current) {
          focusUser(null)
          focusRoom(null)
          setTasksForId(null)
          setNotesForId(null)
          setOfficeFor(null)
          setRequestsOpen(false)
        }
      }
      return next
    })
  }, [focusUser, focusRoom, raiseWin])
  // Store-driven windows (person/room/hire) still come to the front on open.
  useEffect(() => {
    if (focusedUserId) raiseWin('person')
  }, [focusedUserId, raiseWin])
  useEffect(() => {
    if (focusedRoomId) raiseWin('room')
  }, [focusedRoomId, raiseWin])
  useEffect(() => {
    if (hireRoomId) raiseWin('hire')
  }, [hireRoomId, raiseWin])
  useEffect(() => {
    if (requestsOpen) raiseWin('requests')
  }, [requestsOpen, raiseWin])
  useEffect(() => {
    if (directoryOpen) raiseWin('people')
  }, [directoryOpen, raiseWin])
  // Keyboard: Esc peels the stack (menu → detail popups → topmost UNPINNED
  // window); Shift+Esc closes every unpinned window; T/N/O/R toggle their
  // panels (the MMO same-key convention — reopening the last context).
  useEffect(() => {
    const closers = (): Array<[string, () => void]> => {
      const open: Array<[string, () => void]> = []
      if (focusedUserId) open.push(['person', () => focusUser(null)])
      if (focusedRoomId) open.push(['room', () => focusRoom(null)])
      if (tasksForId) open.push(['tasks', () => setTasksForId(null)])
      if (notesForId) open.push(['notes', () => setNotesForId(null)])
      if (officeFor) open.push(['office', () => setOfficeFor(null)])
      if (requestsOpen) open.push(['requests', () => setRequestsOpen(false)])
      if (hireRoomId) open.push(['hire', () => openHire(null)])
      if (directoryOpen) open.push(['people', () => setDirectoryOpen(false)])
      return open
    }
    const zOf = (k: string) => winOver[k]?.z ?? (WIN_DEFS[k]?.idx ?? 0) + 10
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null
      const typing =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      if (e.key === 'Escape') {
        // Esc while typing = "cancel editing", not "close my window with the
        // half-written draft in it" — blur the input; the next Esc peels.
        if (typing) {
          target?.blur()
          return
        }
        if (menuOpen) return setMenuOpen(false)
        if (moreOpen) return setMoreOpen(false)
        if (noteDetail) return setNoteDetail(null)
        if (taskDetail) return setTaskDetail(null)
        if (terminalFor) return setTerminalFor(null)
        const open = closers()
        if (e.shiftKey) {
          for (const [k, close] of open) if (!pinnedWins.includes(k)) close()
          return
        }
        const peelable = open.filter(([k]) => !pinnedWins.includes(k))
        if (peelable.length === 0) return
        peelable.sort((a, b) => zOf(b[0]) - zOf(a[0]))
        peelable[0][1]()
        return
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey || compactRef.current) return
      // "/" opens the people directory (the universal search shortcut).
      if (e.key === '/') {
        e.preventDefault()
        toggleDirectory()
        return
      }
      const key = e.key.toLowerCase()
      // First press with no history falls back to the busiest room / the org's
      // office — a hotkey that does nothing reads as broken.
      if (key === 't') {
        if (tasksForId) setTasksForId(null)
        else {
          const target = lastOpenedRef.current.tasks ?? hotRoomRef.current
          if (target) onTasksFocus(target)
        }
      } else if (key === 'n') {
        if (notesForId) setNotesForId(null)
        else {
          const target = lastOpenedRef.current.notes ?? hotRoomRef.current
          if (target) onNotesFocus(target)
        }
      } else if (key === 'o') {
        if (officeFor) setOfficeFor(null)
        else {
          const target = lastOpenedRef.current.office ?? officeFallbackRef.current
          if (target) onOfficeFocus(target)
        }
      } else if (key === 'r') {
        setRequestsOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    menuOpen,
    moreOpen,
    noteDetail,
    taskDetail,
    terminalFor,
    focusedUserId,
    focusedRoomId,
    tasksForId,
    notesForId,
    officeFor,
    requestsOpen,
    directoryOpen,
    hireRoomId,
    winOver,
    pinnedWins,
    focusUser,
    focusRoom,
    openHire,
    onTasksFocus,
    onNotesFocus,
    onOfficeFocus,
    toggleDirectory,
  ])

  // Clutter budget (OpenTTD): at most WIN_CAP unpinned windows — opening one
  // more silently evicts the oldest (lowest z). Pinned windows are immune.
  useEffect(() => {
    if (compact) return
    const open: Array<[string, () => void]> = []
    if (focusedUserId) open.push(['person', () => focusUser(null)])
    if (focusedRoomId) open.push(['room', () => focusRoom(null)])
    if (tasksForId) open.push(['tasks', () => setTasksForId(null)])
    if (notesForId) open.push(['notes', () => setNotesForId(null)])
    if (officeFor) open.push(['office', () => setOfficeFor(null)])
    if (requestsOpen) open.push(['requests', () => setRequestsOpen(false)])
    if (hireRoomId) open.push(['hire', () => openHire(null)])
    if (directoryOpen) open.push(['people', () => setDirectoryOpen(false)])
    const unpinned = open.filter(([k]) => !pinnedWins.includes(k))
    if (unpinned.length <= WIN_CAP) return
    const zOf = (k: string) => winOver[k]?.z ?? (WIN_DEFS[k]?.idx ?? 0) + 10
    unpinned.sort((a, b) => zOf(a[0]) - zOf(b[0]))
    const [evictKey, close] = unpinned[0]
    // Announce the eviction — a window silently vanishing reads as a bug.
    setCapToast(`${WIN_TITLES[evictKey] ?? evictKey} window closed — 📌 pin windows to keep them`)
    close()
  }, [
    compact,
    focusedUserId,
    focusedRoomId,
    tasksForId,
    notesForId,
    officeFor,
    requestsOpen,
    directoryOpen,
    hireRoomId,
    pinnedWins,
    winOver,
    focusUser,
    focusRoom,
    openHire,
  ])

  useEffect(() => {
    if (!capToast) return
    const t = window.setTimeout(() => setCapToast(null), 4000)
    return () => window.clearTimeout(t)
  }, [capToast])

  const busRef = useRef<EventBus>()
  if (!busRef.current) {
    busRef.current = new EventBus()
    // The panels' avatars hear the looks from the same bus as the floor,
    // from the first emit on.
    followAgentLooks(busRef.current)
  }

  const snap = useQuery({
    queryKey: ['snapshot', organizationId],
    queryFn: () => buildSnapshot(getToken, organizationId),
    // The snapshot is a one-time still frame; realtime drives updates. Don't
    // refetch on focus/staleness — it tore down + re-subscribed the Pusher
    // channels, which broke live delivery.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (snap.data) setSnapshot(snap.data)
    // Hotkey fallback target: the busiest member-bearing team room.
    const hot = (snap.data?.pulses ?? [])
      .filter((p) => p.category === 'TEAM' && p.memberUserIds.length > 0)
      .sort((a, b) => {
        const ta = a.latestUpdate ? Date.parse(a.latestUpdate) || 0 : 0
        const tb = b.latestUpdate ? Date.parse(b.latestUpdate) || 0 : 0
        return tb - ta
      })[0]
    hotRoomRef.current = hot?.id ?? null
  }, [snap.data, setSnapshot])

  const layout = useMemo(
    () => (snap.data ? computeLayout(snap.data) : null),
    // layoutNonce re-applies saved room offsets after an arrange-mode drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [snap.data, layoutNonce],
  )
  const snapshotReady = Boolean(snap.data)

  // Live channels: presence, messages (your private-users feed), per-room tasks.
  // Keyed on the channel-relevant ID SETS, not object identity: a meeting
  // refetch writes a new snapshot object and every arrange drag/style click
  // bumps layout, but the channels only change when the pulse/room sets do —
  // tearing all subscriptions down re-runs private-channel auth and DROPS any
  // event delivered in that window (no replay).
  const pulseKey = (snap.data?.pulses ?? [])
    .map((p) => p.id)
    .sort()
    .join(',')
  const roomKey = (layout?.rooms ?? [])
    // Synthetic ids (meeting tables) have no real channel; a meeting's OWN
    // pulse is covered below via the meetings list.
    .filter((r) => r.category !== 'LOBBY' && !r.id.startsWith('__'))
    .map((r) => r.id)
    .sort()
    .join(',')
  // Meetings can live on pulses that aren't drawn rooms (personal pulses,
  // 13th+ DMs) — subscribe their meeting-session channels too, or their
  // end-event never reaches us and the table stays "live" until reload.
  const meetingKey = [
    ...new Set((snap.data?.meetings ?? []).map((m) => m.pulseId).filter(Boolean)),
  ]
    .sort()
    .join(',')
  useEffect(() => {
    const bus = busRef.current
    if (!snapshotReady || !bus) return
    const pusher = getPusher(getToken)
    const meetingRooms = new Set(
      [...roomKey.split(','), ...meetingKey.split(',')].filter(Boolean),
    )
    const off = subscribeWorld({
      pusher,
      organizationId,
      userId: me?.id ?? null,
      pulseIds: pulseKey.split(',').filter(Boolean),
      roomPulseIds: [...meetingRooms],
      teamPulses: [],
      bus,
    })
    return off
    // snapshotReady + the key strings are the real inputs; snap.data identity
    // churns on every meetings write and must not resubscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotReady, organizationId, getToken, me?.id, pulseKey, roomKey, meetingKey])

  // Ambient choreography over the real roster so the office always looks alive.
  // Skipped in "Live only" mode, which shows only real Pusher events.
  useEffect(() => {
    const bus = busRef.current
    if (!snap.data || !bus || liveOnly) return
    return startAmbient(snap.data, bus)
  }, [snap.data, liveOnly])

  // Meeting realtime: a meeting-session event fired on a room's pulse. Re-resolve
  // by refetching ONLY meetings and writing a fresh snapshot into the cache — the
  // scene restarts deterministically (computeLayout is pure) without a reload.
  // Coalesced so a burst of events triggers one refetch. Depends on a boolean
  // (not snap.data itself) so writing the refetched snapshot doesn't tear down
  // and re-register this very listener on every refetch.
  useEffect(() => {
    const bus = busRef.current
    if (!snapshotReady || !bus) return
    let pending: ReturnType<typeof setTimeout> | undefined
    let cancelled = false
    const refetch = async (): Promise<void> => {
      try {
        const token = await getToken()
        const meetings = await fetchMeetings(createClient(token), organizationId)
        if (cancelled) return
        queryClient.setQueryData<WorldSnapshot>(
          ['snapshot', organizationId],
          (prev) => {
            if (!prev) return prev
            // Unchanged meetings → same snapshot reference → no scene restart
            // (a restart blinks the world and resets the camera mid-demo).
            if (JSON.stringify(prev.meetings) === JSON.stringify(meetings)) {
              return prev
            }
            return withDemoMeetings({ ...prev, meetings })
          },
        )
      } catch {
        // Meetings unavailable — leave the current floor plan as-is.
      }
    }
    const off = bus.on((event) => {
      if (event.kind !== 'meetingRefetch') return
      if (pending) clearTimeout(pending)
      pending = setTimeout(() => void refetch(), 600)
    })
    return () => {
      cancelled = true
      if (pending) clearTimeout(pending)
      off()
    }
  }, [snapshotReady, organizationId, getToken, queryClient])

  // Realtime gap recovery: pusher-js reconnects by itself, but events that
  // fired while the socket was down are gone forever — and a tab Chrome froze
  // (or a laptop that slept) can sit disconnected past the retry backoff. On
  // reconnect, re-pull the poll-backed state (wall sweeps via resyncNonce,
  // meetings inline); on tab-visible with a dead socket, reconnect NOW instead
  // of waiting out the backoff.
  const [resyncNonce, setResyncNonce] = useState(0)
  useEffect(() => {
    if (!snapshotReady) return
    const pusher = getPusher(getToken)
    let wasDown = false
    const resync = async (): Promise<void> => {
      setResyncNonce((n) => n + 1)
      try {
        const token = await getToken()
        const meetings = await fetchMeetings(createClient(token), organizationId)
        queryClient.setQueryData<WorldSnapshot>(
          ['snapshot', organizationId],
          (prev) => (prev ? withDemoMeetings({ ...prev, meetings }) : prev),
        )
      } catch {
        // Meetings unavailable — the floor plan stays as-is.
      }
    }
    const onState = (states: { previous: string; current: string }): void => {
      if (states.current === 'connected' && wasDown) {
        wasDown = false
        void resync()
      } else if (
        states.current === 'unavailable' ||
        states.current === 'failed' ||
        states.current === 'disconnected'
      ) {
        wasDown = true
      }
    }
    pusher.connection.bind('state_change', onState)
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return
      const state = pusher.connection.state
      if (state !== 'connected' && state !== 'connecting') pusher.connect()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      pusher.connection.unbind('state_change', onState)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [snapshotReady, getToken, organizationId, queryClient])

  // Agent looks (the role's emblem: HR, PA, EA, Steward ★; the stored look;
  // unplugged) for EVERY viewer. Two sources: the member-visible presence
  // feed (everyone; the only one with the stored look and readiness) and the
  // owner-only agentAccounts poll (owners). Each tick stores its source and
  // emits the merge: personal when either says so, the rest from the fresher
  // that can.
  const looksRef = useRef<{
    orgId: string
    presence?: AgentLooksSource
    accounts?: AgentLooksSource
  }>({ orgId: organizationId })
  const publishLooks = useCallback(
    (source: 'presence' | 'accounts', looks: AgentLooksSource) => {
      const bus = busRef.current
      if (!bus) return
      if (looksRef.current.orgId !== organizationId) {
        looksRef.current = { orgId: organizationId }
      }
      looksRef.current[source] = looks
      const merged = mergeAgentLooks([
        looksRef.current.presence,
        looksRef.current.accounts,
      ])
      // Full sets each emit — the scene reconciles on/off.
      bus.emit({ kind: 'agentHr', userIds: merged.hr })
      bus.emit({ kind: 'agentPersonal', userIds: merged.personal })
      bus.emit({ kind: 'agentEa', userIds: merged.ea })
      bus.emit({
        kind: 'agentSteward',
        userIds: merged.steward,
        staffUserIds: merged.stewardStaff,
      })
      bus.emit({ kind: 'agentStyle', styles: merged.styles })
      bus.emit({ kind: 'agentUnplugged', userIds: merged.unplugged })
    },
    [organizationId],
  )

  // The member-visible looks feed, polled like the owner sweep below.
  useEffect(() => {
    if (!snapshotReady) return
    let stopped = false
    const tick = async () => {
      try {
        const looks = await fetchAgentLooks(
          createClient(await getToken()),
          organizationId,
        )
        if (!stopped) publishLooks('presence', { at: Date.now(), ...looks })
      } catch {
        // Transient: the last looks stay on the floor; try again next tick.
      }
    }
    void tick()
    const timer = window.setInterval(() => void tick(), 60_000)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [snapshotReady, organizationId, getToken, publishLooks])

  // Ambient agent presence: poll each agent's latest box activity (owner-gated
  // server-side) and float fresh work as 💭 thoughts above their characters.
  useEffect(() => {
    const bus = busRef.current
    if (!snapshotReady || !bus) return
    const owner =
      snap.data?.people.find((p) => p.userId === me?.id)?.role === 'OWNER'
    if (!owner) return
    let stopped = false
    const lastSeen = new Map<string, string>()
    let first = true
    const tick = async () => {
      try {
        const client = createClient(await getToken())
        const accounts = await fetchAgentAccountsAll(client, organizationId)
        // The looks, second source (owners): merged with the presence feed's.
        if (!stopped) {
          const running = accounts.filter((a) => a.status === 'ACTIVE')
          // An older API cannot say who is a Steward (undefined): leave that
          // to the presence feed instead of taking every badge off.
          const knowsSteward = accounts.every((a) => a.steward !== undefined)
          const ids = (pick: (a: (typeof running)[number]) => boolean) =>
            running.filter(pick).map((a) => a.userId)
          publishLooks('accounts', {
            at: Date.now(),
            hr: ids((a) => a.canManageAgents),
            personal: ids((a) => a.roleKind === 'PERSONAL'),
            ea: ids((a) => a.roleKind === 'EA'),
            steward: knowsSteward ? ids((a) => Boolean(a.steward)) : null,
            stewardStaff: knowsSteward
              ? ids((a) => Boolean(a.steward?.staff))
              : null,
            // The stored looks and readiness come from the presence feed.
            styles: null,
            unplugged: null,
          })
        }
        const active = accounts.filter((a) => a.status === 'ACTIVE').slice(0, 12)
        await Promise.all(
          active.map(async (a) => {
            try {
              const [acts, brief] = await Promise.all([
                fetchAgentActivities(client, a.id, 1),
                // Learning mode + inbox depth for the in-world badges.
                fetchAgentBrief(client, a.id).catch(() => null),
              ])
              if (stopped) return
              if (brief) {
                bus.emit({
                  kind: 'agentMode',
                  userId: a.userId,
                  mode: brief.mode,
                  inboxCount: brief.inboxCount,
                })
              }
              const latest = acts[0]
              if (!latest) return
              const prev = lastSeen.get(a.id)
              lastSeen.set(a.id, latest.id)
              // Seed silently on the first pass unless the work is FRESH, so
              // stale summaries don't float on load.
              const fresh =
                Date.now() - parseApiTime(latest.createdAt) < 10 * 60_000
              // Bubble the HUMANIZED line (AGENT_PRESENCE_SPEC.md §4G) —
              // raw tool names never float on the floor.
              const line = latest.label ?? latest.summary
              if ((first ? fresh : prev !== latest.id) && line) {
                bus.emit({
                  kind: 'agentWork',
                  userId: a.userId,
                  summary: line,
                })
              }
            } catch {
              // One agent's feed failing must not break the sweep.
            }
          }),
        )
        // One org-wide call: everything agents are waiting on humans for.
        try {
          const requests = await fetchAgentRequests(client, organizationId)
          if (!stopped) {
            setNeedsYou(requests.total)
            const waiting = new Set<string>()
            for (const n of requests.notebooks) waiting.add(n.agentAccountId)
            for (const d of requests.skillDrafts)
              if (d.agentAccountId) waiting.add(d.agentAccountId)
            for (const u of requests.skillUpdates) waiting.add(u.agentAccountId)
            for (const a of accounts) {
              bus.emit({
                kind: 'agentNeedsYou',
                userId: a.userId,
                on: waiting.has(a.id),
              })
            }
          }
        } catch {
          // Requests query is owner-gated — fail soft, chip stays hidden.
        }
        // Office-box health → the in-world server racks' LEDs.
        try {
          const offices = await fetchOffices(client, organizationId)
          officeFallbackRef.current =
            offices.find((o) => o.status === 'ACTIVE')?.id ?? offices[0]?.id ?? null
          await Promise.all(
            offices
              .filter((o) => o.status === 'ACTIVE')
              .slice(0, 6)
              .map(async (o) => {
                try {
                  const health = await fetchOfficeHealth(client, o.id)
                  if (!stopped) {
                    bus.emit({
                      kind: 'officeHealth',
                      officeId: o.id,
                      name: o.name,
                      level: health.level,
                      issues: health.issues,
                    })
                  }
                } catch {
                  // One office's checklist failing must not break the sweep.
                }
              }),
          )
        } catch {
          // Offices query is owner-gated — fail soft, no racks for viewers.
        }
        first = false
      } catch {
        // Not an owner after all / transient — try again next tick.
      }
    }
    void tick()
    const timer = window.setInterval(() => void tick(), 60_000)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
    // snap.data read via closure; the roster of agents changes only with the
    // snapshot, and snapshotReady covers the initial load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotReady, organizationId, getToken, me?.id, publishLooks])

  // Event chips: real room activity stacks on the left edge and expires.
  useEffect(() => {
    if (!snapshotReady || compact) return
    const bus = busRef.current
    if (!bus) return
    const teamNames = new Map(
      (snap.data?.pulses ?? [])
        .filter((p) => p.category === 'TEAM')
        .map((p) => [p.id, p.name]),
    )
    const off = bus.on((event) => {
      let pulseId: string | null = null
      let text = ''
      if (event.kind === 'message' && !event.ambient) {
        pulseId = event.pulseId
        text = event.text ? event.text.slice(0, 34) : 'new message'
      } else if (event.kind === 'task' && !event.ambient) {
        pulseId = event.pulseId
        text =
          event.action === 'created' ? 'task added' : `task ${event.action}`
      } else if (event.kind === 'note') {
        pulseId = event.pulseId
        const label =
          event.action === 'created' ? 'note added' : `note ${event.action}`
        text = event.title ? `${label} — ${event.title.slice(0, 24)}` : label
      }
      if (!pulseId || !teamNames.has(pulseId)) return
      const name = teamNames.get(pulseId)!
      setChips((c) => ({
        ...c,
        [pulseId!]: {
          count:
            (c[pulseId!]?.at ?? 0) > Date.now() - 30_000
              ? (c[pulseId!]?.count ?? 0) + 1
              : 1,
          text,
          at: Date.now(),
          name,
        },
      }))
    })
    const prune = window.setInterval(() => {
      setChips((c) => {
        const now = Date.now()
        const next = Object.fromEntries(
          Object.entries(c).filter(([, v]) => now - v.at < 30_000),
        )
        return Object.keys(next).length === Object.keys(c).length ? c : next
      })
    }, 5_000)
    return () => {
      off()
      window.clearInterval(prune)
    }
    // snap.data via closure — the TEAM set changes only with the snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotReady, compact])

  // Wall furniture data: task counts + note counts per team room. Tasks and
  // notes both refresh instantly on their pulse events (debounced per pulse);
  // the 60s sweep covers rooms beyond the subscribe set and API gaps.
  useEffect(() => {
    if (!snapshotReady) return
    const bus = busRef.current
    if (!bus) return
    let stopped = false
    const noteMarks = new Map<string, string>()
    const pending = new Map<string, number>()

    const teamRooms = () =>
      (snap.data?.pulses ?? [])
        .filter((p) => p.category === 'TEAM' && p.memberUserIds.length > 0)
        .sort((a, b) => {
          const ta = a.latestUpdate ? Date.parse(a.latestUpdate) || 0 : 0
          const tb = b.latestUpdate ? Date.parse(b.latestUpdate) || 0 : 0
          return tb - ta
        })
        .slice(0, 12)

    const pushTasks = async (pulseId: string): Promise<void> => {
      try {
        const client = createClient(await getToken())
        const board = await fetchRoomTasks(client, { organizationId, pulseId })
        if (stopped) return
        const counts = { open: 0, doing: 0, done: 0, overdue: 0 }
        for (const t of board.tasks) {
          if (t.type === 'LIST') continue
          if (t.status === 'COMPLETED') counts.done += 1
          else if (t.status === 'INPROGRESS') counts.doing += 1
          else if (t.status === 'OVERDUE') counts.overdue += 1
          else if (t.status !== 'CANCELLED') counts.open += 1
        }
        bus.emit({ kind: 'taskBoard', pulseId, ...counts })
      } catch {
        // Fail soft — the board keeps its last state.
      }
    }

    const pushNotes = async (pulseId: string): Promise<void> => {
      try {
        const client = createClient(await getToken())
        const { notes, total } = await fetchNotes(client, {
          organizationId,
          pulseId,
          perPage: 1,
        })
        if (stopped) return
        const mark = `${total}:${notes[0]?.updatedAt ?? ''}`
        const prev = noteMarks.get(pulseId)
        noteMarks.set(pulseId, mark)
        bus.emit({
          kind: 'notesShelf',
          pulseId,
          count: total,
          fresh: prev !== undefined && prev !== mark,
        })
      } catch {
        // Fail soft.
      }
    }

    const sweep = async (): Promise<void> => {
      for (const room of teamRooms()) {
        if (stopped) return
        await Promise.all([pushTasks(room.id), pushNotes(room.id)])
      }
    }
    void sweep()
    const timer = window.setInterval(() => void sweep(), 60_000)

    // Task/note events → refresh that room's wall shortly after (coalesced),
    // and refresh any open panel showing that pulse's list.
    const off = bus.on((event) => {
      if (event.kind !== 'task' && event.kind !== 'note') return
      // Showcase's fabricated task events must not trigger real API refetches
      // (~6 spurious queries/min, forever) — the scene animates them, we don't.
      if (event.kind === 'task' && event.ambient) return
      const { kind, pulseId } = event
      const key = `${kind}:${pulseId}`
      if (pending.has(key)) return
      pending.set(
        key,
        window.setTimeout(() => {
          pending.delete(key)
          if (kind === 'task') {
            void pushTasks(pulseId)
            void queryClient.invalidateQueries({ queryKey: ['roomTasks', pulseId] })
          } else {
            void pushNotes(pulseId)
            void queryClient.invalidateQueries({ queryKey: ['roomNotes', pulseId] })
          }
        }, 1500),
      )
    })

    return () => {
      stopped = true
      window.clearInterval(timer)
      off()
      for (const t of pending.values()) window.clearTimeout(t)
    }
    // snap.data read via closure; room set changes only with the snapshot.
    // resyncNonce bumps after a Pusher reconnect — re-run the sweep to catch
    // anything that changed while the socket was down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotReady, organizationId, getToken, resyncNonce])

  if (snap.isLoading) return <Loading label="Reading the room…" />
  if (snap.isError || !snap.data || !busRef.current)
    return (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 300,
            padding: 20,
            background: theme.panel,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            textAlign: 'center',
            color: theme.text,
          }}
        >
          <div style={{ fontSize: 22, marginBottom: 8 }}>🏢</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.cream }}>
            Couldn't reach your office
          </div>
          <div style={{ fontSize: 11.5, color: theme.textDim, margin: '6px 0 14px' }}>
            The connection or your session may have expired.
          </div>
          <button
            onClick={() => void snap.refetch()}
            style={{
              width: '100%',
              padding: '9px 0',
              background: theme.terracotta,
              border: 'none',
              borderRadius: 9,
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
          {!embedded && (
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              style={{
                width: '100%',
                marginTop: 8,
                padding: '8px 0',
                background: 'none',
                border: `1px solid ${theme.border}`,
                borderRadius: 9,
                color: theme.textDim,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Sign out
            </button>
          )}
        </div>
      </div>
    )

  const { people } = snap.data
  const roomCount = layout?.rooms.length ?? 0
  const agents = people.filter((p) => p.isAgent)
  const online = people.filter((p) => p.presence === 'online')
  const orgName =
    me?.organizations.find((o) => o.id === organizationId)?.name ?? 'Organization'
  const focused = people.find((p) => p.userId === focusedUserId) ?? null
  const focusedRoom = snap.data.pulses.find((p) => p.id === focusedRoomId) ?? null
  const hireRoom = snap.data.pulses.find((p) => p.id === hireRoomId) ?? null
  // Hiring agents is an org-owner capability (the API enforces it too).
  const canHire = people.find((p) => p.userId === me?.id)?.role === 'OWNER'
  const focusedTasks = focused
    ? Object.values(snap.data.tasksByPulse).reduce(
        (n, list) =>
          n + list.filter((t) => t.assigneeUserIds.includes(focused.userId)).length,
        0,
      )
    : 0
  const focusedRooms = focused
    ? snap.data.pulses
        .filter(
          (p) => p.category === 'TEAM' && p.memberUserIds.includes(focused.userId),
        )
        .map((p) => p.name)
    : []
  // Where a ping/message defaults to: the pulse you clicked them in. An open
  // room panel is the strongest signal; else the room they're seated in.
  const contextPulseId =
    focused && focusedRoomId && focusedRoom?.memberUserIds.includes(focused.userId)
      ? focusedRoomId
      : (focused && layout?.byUser?.[focused.userId]?.roomId) || null
  const personDestinations =
    focused && me?.id
      ? buildMessageDestinations({
          pulses: snap.data.pulses,
          meId: me.id,
          targetUserId: focused.userId,
          targetName: focused.name,
          contextPulseId,
        })
      : []
  // Adapt the delivery layer's notify hooks to the real hub client. Messages
  // are ALSO server-triggered, but typing + reactions are client-only — without
  // these the recipient never sees the wave or the "…is typing".
  const hubNotify = (meId: string, meName: string): DeliveryNotify => {
    const hub = getHub(getToken)
    return {
      message: (a) =>
        hub.notifyTeamMessage({
          pulseId: a.pulseId,
          pulseName: a.pulseName,
          senderId: meId,
          senderName: meName,
          messageId: a.messageId,
          messagePreview: a.preview,
          organizationId,
          teamThreadId: a.teamThreadId,
          mentionedUserIds: a.mentionedUserIds,
        }),
      reaction: (a) =>
        hub.notifyReaction({
          pulseId: a.pulseId,
          messageId: a.messageId,
          emoji: a.emoji,
          userId: meId,
          userName: meName,
          organizationId,
        }),
    }
  }
  // Directory grouping: each person under the room the map seats them in (so
  // clicking jumps to that room), falling back to their first team, else
  // Commons. roomOrder mirrors the floor top-to-bottom.
  const pulseName = new Map(snap.data.pulses.map((p) => [p.id, p.name]))
  const roomOrder = (layout?.rooms ?? [])
    .filter((r) => r.category === 'TEAM')
    .map((r) => pulseName.get(r.id))
    .filter((n): n is string => Boolean(n))
  const seatRoomName: Record<string, string> = {}
  for (const p of people) {
    const seatRid = layout?.byUser?.[p.userId]?.roomId
    const seatName = seatRid
      ? pulseName.get(seatRid) ??
        (seatRid === AGENTS_ID ? 'Agent Bay' : undefined)
      : undefined
    const firstTeam = snap.data.pulses.find(
      (x) => x.category === 'TEAM' && x.memberUserIds.includes(p.userId),
    )
    seatRoomName[p.userId] = seatName ?? firstTeam?.name ?? 'Commons'
  }
  // On phones, sheets own the bottom of the screen — hide the ticker, hint and
  // zoom buttons underneath them instead of stacking overlapping chrome.
  const tasksFor = tasksForId
    ? snap.data.pulses.find((p) => p.id === tasksForId) ?? null
    : null
  const notesFor = notesForId
    ? snap.data.pulses.find((p) => p.id === notesForId) ?? null
    : null
  const anyPanelOpen = Boolean(
    focused ||
      focusedRoom ||
      hireRoom ||
      requestsOpen ||
      directoryOpen ||
      terminalFor ||
      officeFor ||
      tasksFor ||
      notesFor,
  )
  // Topmost open window (drives the same-gesture toggle + glass dimming).
  const openKeys: string[] = []
  if (focused) openKeys.push('person')
  if (focusedRoom) openKeys.push('room')
  if (tasksFor) openKeys.push('tasks')
  if (notesFor) openKeys.push('notes')
  if (officeFor) openKeys.push('office')
  if (requestsOpen) openKeys.push('requests')
  if (hireRoom) openKeys.push('hire')
  if (directoryOpen) openKeys.push('people')
  const winZ = (k: string): number => winOver[k]?.z ?? (WIN_DEFS[k]?.idx ?? 0) + 10
  const topKey =
    openKeys.length > 0
      ? openKeys.reduce((best, k) => (winZ(k) > winZ(best) ? k : best))
      : null
  openSnapRef.current = {
    focusedUserId,
    focusedRoomId,
    tasksForId,
    notesForId,
    officeFor,
    topKey,
  }

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%', overflow: 'hidden' }}>
      <PhaserGame
        snapshot={snap.data}
        bus={busRef.current}
        onFocus={onPersonFocus}
        onRoomFocus={onRoomFocusToggle}
        onHire={onHireOpen}
        onOfficeFocus={onOfficeFocus}
        onTasksFocus={onTasksFocus}
        onNotesFocus={onNotesFocus}
        onHoverPerson={onHoverPerson}
        canHire={canHire}
        arrangeMode={arrangeMode}
        onRoomMoved={onRoomMoved}
        layoutNonce={layoutNonce}
        selfUserId={me?.id}
      />

      <header
        style={{
          ...hudBar,
          padding: compact ? '10px 12px' : '9px 16px',
          gap: 8,
          // Desktop is a single tidy row (secondary controls live in ⋯);
          // compact may wrap its two clusters.
          flexWrap: compact ? 'wrap' : 'nowrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 9 : 12, minWidth: 0 }}>
          {!embedded && <div style={logoMark}>Z</div>}
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Embedded, the dashboard already names the workspace — the
                  bar keeps only the working controls. */}
              {!embedded && (
                <span
                  style={{
                    fontSize: compact ? 14 : 16,
                    fontWeight: 600,
                    color: theme.cream,
                    maxWidth: compact ? 150 : 280,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {orgName}
                </span>
              )}
              <div style={segWrap} role="group" aria-label="Activity mode">
                <button
                  onClick={() => setMode(false)}
                  style={{ ...(liveOnly ? segIdle : segActiveShowcase), ...(compact ? segCompact : null) }}
                  title="Showcase — simulated office life layered over your live data."
                >
                  <span style={liveOnly ? segDotIdle : showcaseDot} />
                  Showcase
                </button>
                <button
                  onClick={() => setMode(true)}
                  style={{ ...(liveOnly ? segActiveLive : segIdle), ...(compact ? segCompact : null) }}
                  title="Live — only real Zunou activity, no simulation."
                >
                  <span style={liveOnly ? liveDot : segDotIdle} />
                  Live
                </button>
              </div>
            </div>
            {!compact && !embedded && <div style={subline}>Glass Office · {config.envName}</div>}
          </div>
        </div>
        {compact ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {needsYou > 0 && (
              <button
                onClick={() => {
                  setRequestsOpen((v) => !v)
                  focusUser(null)
                  focusRoom(null)
                  // Compact shows ONE sheet, ordered tasks→…→requests: any
                  // sheet left open would render instead and the ✋ button
                  // would look dead.
                  setTasksForId(null)
                  setNotesForId(null)
                  setOfficeFor(null)
                  setMenuOpen(false)
                }}
                style={{
                  ...ghostBtn,
                  color: '#ffd28a',
                  borderColor: '#4a3d1e',
                  background: '#241f12',
                  fontWeight: 700,
                  padding: '9px 12px',
                  fontSize: 12.5,
                  borderRadius: 10,
                }}
              >
                ✋ {needsYou}
              </button>
            )}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menu"
              style={{
                ...ghostBtn,
                padding: '9px 14px',
                fontSize: 15,
                borderRadius: 10,
                ...(menuOpen
                  ? { color: theme.cream, background: '#232633' }
                  : null),
              }}
            >
              ☰
            </button>
          </div>
        ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Stat color={theme.textDim} value={roomCount} label="rooms" compact={compact} />
          <Stat color={theme.online} value={online.length} label="online" dot compact={compact} />
          <Stat color={theme.agent} value={agents.length} label="agents" dot compact={compact} />
          <span style={navDivider} />
          <button
            onClick={toggleDirectory}
            title="Find people — search the org and jump to anyone on the floor (/)"
            style={{
              ...ghostBtn,
              ...(directoryOpen
                ? { color: theme.cream, background: '#232633' }
                : null),
            }}
          >
            🔍 Find
          </button>
          {needsYou > 0 && (
            <button
              onClick={() => {
                setRequestsOpen((v) => !v)
                focusUser(null)
                focusRoom(null)
              }}
              title="Lessons and skills your agents are waiting on you to review"
              style={{
                ...ghostBtn,
                color: '#ffd28a',
                borderColor: '#4a3d1e',
                background: '#241f12',
                fontWeight: 700,
              }}
            >
              ✋ {needsYou} need you
            </button>
          )}
          <button
            onClick={() => setArrangeMode((v) => !v)}
            title={
              arrangeMode
                ? 'Done arranging — back to normal interaction'
                : 'Arrange — drag rooms to rearrange the floor (saved for this org)'
            }
            style={{
              ...ghostBtn,
              ...(arrangeMode
                ? { color: '#c9c3ff', borderColor: '#34306b', background: '#191634' }
                : null),
            }}
          >
            {arrangeMode ? '✓ Done' : 'Arrange'}
          </button>
          <button
            onClick={() => setMoreOpen((v) => !v)}
            aria-label="More"
            title="Replay, sound, switch org, sign out"
            style={{
              ...ghostBtn,
              padding: '6px 10px',
              fontWeight: 700,
              letterSpacing: 1,
              ...(moreOpen ? { color: theme.cream, background: '#232633' } : null),
            }}
          >
            ⋯
          </button>
        </div>
        )}
      </header>

      {/* Desktop overflow menu: the occasional controls, out of the bar. */}
      {!compact && moreOpen && (
        <>
          <div style={menuBackdrop} onClick={() => setMoreOpen(false)} />
          <div style={{ ...flyMenu, top: 58, right: 16 }}>
            <div onClickCapture={() => setMoreOpen(false)}>
              <ReplayControl
                bus={busRef.current}
                snapshot={snap.data}
                organizationId={organizationId}
                getToken={getToken}
                buttonStyle={menuItem}
              />
            </div>
            <button onClick={toggleSound} style={menuItem}>
              {sound ? '🔊 Sound on' : '🔇 Sound off'}
            </button>
            {!embedded && (
              <>
                <button
                  onClick={() => {
                    setMoreOpen(false)
                    reset()
                  }}
                  style={menuItem}
                >
                  Switch org
                </button>
                <button
                  onClick={() => {
                    setStoredToken('')
                    void logout({ logoutParams: { returnTo: window.location.origin } })
                  }}
                  style={menuItem}
                >
                  Sign out
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* Arrange toolbar: the styling tools live HERE, not in the nav — they
          used to explode across the title and the canvas. */}
      {!compact && arrangeMode && (
        <div style={arrangeBar}>
          <span style={{ fontSize: 11, color: theme.textDim }}>Office style</span>
          {(Object.keys(STYLES) as OfficeStyle[]).map((s) => (
            <button
              key={s}
              onClick={() => {
                setOfficeStyle(organizationId, s)
                setOfficeStyleState(s)
                setLayoutNonce((n) => n + 1)
              }}
              style={{
                ...ghostBtn,
                padding: '5px 10px',
                fontSize: 11.5,
                ...(officeStyle === s
                  ? { color: '#ffd28a', borderColor: '#4a3d1e', background: '#241f12' }
                  : null),
              }}
            >
              {STYLES[s].label}
            </button>
          ))}
          <span style={navDivider} />
          {hasRoomOffsets(organizationId) && (
            <button
              onClick={() => {
                clearRoomOffsets(organizationId)
                setLayoutNonce((n) => n + 1)
              }}
              title="Reset the floor plan to the default layout"
              style={{ ...ghostBtn, padding: '5px 10px', fontSize: 11.5 }}
            >
              Reset layout
            </button>
          )}
          <button
            onClick={resetWindows}
            title="Forget saved window positions, pins and collapses"
            style={{ ...ghostBtn, padding: '5px 10px', fontSize: 11.5 }}
          >
            Reset windows
          </button>
        </div>
      )}

      {/* Compact fly-over: all the HUD controls in one tidy stack. */}
      {compact && menuOpen && (
        <>
          <div style={menuBackdrop} onClick={() => setMenuOpen(false)} />
          <div style={flyMenu}>
            <div style={{ fontSize: 11.5, color: theme.textDim, padding: '2px 4px 8px' }}>
              {roomCount} rooms · {online.length} online · {agents.length} agents
            </div>
            <button
              onClick={() => {
                toggleDirectory()
                setMenuOpen(false)
              }}
              style={menuItem}
            >
              🔍 Find people
            </button>
            <ReplayControl
              bus={busRef.current}
              snapshot={snap.data}
              organizationId={organizationId}
              getToken={getToken}
              buttonStyle={menuItem}
            />
            <button
              onClick={() => setArrangeMode((v) => !v)}
              style={{
                ...menuItem,
                ...(arrangeMode
                  ? { color: '#c9c3ff', borderColor: '#34306b', background: '#191634' }
                  : null),
              }}
            >
              {arrangeMode ? 'Done arranging' : 'Arrange rooms'}
            </button>
            {arrangeMode && (
              <div style={{ display: 'flex', gap: 5 }}>
                {(Object.keys(STYLES) as OfficeStyle[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setOfficeStyle(organizationId, s)
                      setOfficeStyleState(s)
                      setLayoutNonce((n) => n + 1)
                    }}
                    style={{
                      ...menuItem,
                      flex: 1,
                      fontSize: 11,
                      padding: '9px 4px',
                      textAlign: 'center',
                      ...(officeStyle === s
                        ? { color: '#ffd28a', borderColor: '#4a3d1e', background: '#241f12' }
                        : null),
                    }}
                  >
                    {STYLES[s].label}
                  </button>
                ))}
              </div>
            )}
            {arrangeMode && hasRoomOffsets(organizationId) && (
              <button
                onClick={() => {
                  clearRoomOffsets(organizationId)
                  setLayoutNonce((n) => n + 1)
                }}
                style={menuItem}
              >
                Reset layout
              </button>
            )}
            <button onClick={toggleSound} style={menuItem}>
              {sound ? '🔊 Sound on' : '🔇 Sound off'}
            </button>
            {!embedded && (
              <>
                <button onClick={reset} style={menuItem}>
                  Switch org
                </button>
                <button
                  onClick={() => {
                    setStoredToken('')
                    void logout({ logoutParams: { returnTo: window.location.origin } })
                  }}
                  style={menuItem}
                >
                  Sign out
                </button>
              </>
            )}
          </div>
        </>
      )}
      <style>{`@keyframes go-live-pulse { 0%,100% { opacity: 1; transform: scale(1) } 50% { opacity: 0.35; transform: scale(0.78) } }
@keyframes go-ticker-in { from { opacity: 0; transform: translateX(-50%) translateY(6px) } to { opacity: 1; transform: translateX(-50%) translateY(0) } }`}</style>

      {(() => {
        // The side panels as windows: draggable frames on desktop, a single
        // bottom sheet on phones (first open wins, others already cleared by
        // the open callbacks).
        const windows: Array<{ key: string; title: string; node: React.ReactNode }> = []
        void 0
        if (focused) {
          windows.push({
            key: 'person',
            // ✦ only beside a picture: a pictureless agent's character says it below.
            title: `${focused.isAgent && !showsCharacter(focused) ? '✦ ' : ''}${focused.name}`,
            node: (
              <DetailPanel
                person={focused}
                openTasks={focusedTasks}
                rooms={focusedRooms}
                onClose={() => focusUser(null)}
                compact={compact}
                isSelf={focused.userId === me?.id}
                onLocate={() =>
                  busRef.current?.emit({ kind: 'cameraFollow', userId: focused.userId })
                }
                onTerminal={
                  focused.isAgent && canHire ? () => setTerminalFor(focused) : undefined
                }
                destinations={personDestinations}
                onMessage={async (destination, text) => {
                  if (!me?.id) throw new Error('Still signing you in — try again')
                  const client = createClient(await getToken())
                  await deliverMessage(
                    client,
                    {
                      destination,
                      organizationId,
                      meId: me.id,
                      targetUserId: focused.userId,
                      targetName: focused.name,
                      pulses: snap.data?.pulses ?? [],
                      text,
                    },
                    hubNotify(me.id, me.name),
                  )
                }}
                onPing={async (destination, emoji) => {
                  if (!me?.id) throw new Error('Still signing you in — try again')
                  const client = createClient(await getToken())
                  return pingPerson(
                    client,
                    {
                      destination,
                      organizationId,
                      meId: me.id,
                      targetUserId: focused.userId,
                      targetName: focused.name,
                      pulses: snap.data?.pulses ?? [],
                      emoji,
                    },
                    hubNotify(me.id, me.name),
                  )
                }}
                onTyping={(destination, isTyping) => {
                  if (!me?.id || !destination.pulseId) return
                  getHub(getToken).notifyTyping({
                    pulseId: destination.pulseId,
                    userId: me.id,
                    userName: me.name,
                    organizationId,
                    isTyping,
                  })
                }}
                manage={
                  focused.isAgent && canHire ? (
                    <AgentManage
                      key={focused.userId}
                      person={focused}
                      organizationId={organizationId}
                      getToken={getToken}
                    />
                  ) : null
                }
                peek={
                  <PersonPeek
                    key={focused.userId}
                    person={focused}
                    organizationId={organizationId}
                    getToken={getToken}
                    isOwner={canHire}
                  />
                }
              />
            ),
          })
        }
        if (focusedRoom) {
          windows.push({
            key: 'room',
            title: focusedRoom.name,
            node: (
              <RoomPanel
                key={focusedRoom.id}
                pulse={focusedRoom}
                organizationId={organizationId}
                meId={me?.id ?? null}
                people={people}
                bus={busRef.current!}
                getToken={getToken}
                onClose={() => focusRoom(null)}
                onFocusPerson={focusUser}
                onLocate={() =>
                  busRef.current?.emit({ kind: 'cameraRoom', pulseId: focusedRoom.id })
                }
                isOwner={canHire}
                compact={compact}
              />
            ),
          })
        }
        if (tasksFor) {
          windows.push({
            key: 'tasks',
            title: `▤ ${tasksFor.name} tasks`,
            node: (
              <TasksPanel
                key={tasksFor.id}
                pulse={tasksFor}
                organizationId={organizationId}
                people={people}
                bus={busRef.current!}
                getToken={getToken}
                onClose={() => setTasksForId(null)}
                onOpenTask={(task) => setTaskDetail({ task, pulseId: tasksFor.id })}
                compact={compact}
              />
            ),
          })
        }
        if (notesFor) {
          windows.push({
            key: 'notes',
            title: `📚 ${notesFor.name} notes`,
            node: (
              <NotesPanel
                key={notesFor.id}
                pulse={notesFor}
                organizationId={organizationId}
                meId={me?.id ?? null}
                getToken={getToken}
                onClose={() => setNotesForId(null)}
                onOpenNote={(note) => setNoteDetail({ note, pulseId: notesFor.id })}
                compact={compact}
              />
            ),
          })
        }
        if (officeFor) {
          windows.push({
            key: 'office',
            title: '🖥 Office health',
            node: (
              <OfficePanel
                key={officeFor}
                officeId={officeFor}
                organizationId={organizationId}
                getToken={getToken}
                onClose={() => setOfficeFor(null)}
                compact={compact}
              />
            ),
          })
        }
        if (requestsOpen) {
          windows.push({
            key: 'requests',
            title: '✋ Agents need you',
            node: (
              <RequestsPanel
                organizationId={organizationId}
                getToken={getToken}
                onClose={() => setRequestsOpen(false)}
                compact={compact}
              />
            ),
          })
        }
        if (hireRoom) {
          windows.push({
            key: 'hire',
            title: `＋ Hire · ${hireRoom.name}`,
            node: (
              <HirePanel
                key={hireRoom.id}
                pulse={hireRoom}
                organizationId={organizationId}
                getToken={getToken}
                onClose={() => openHire(null)}
                compact={compact}
              />
            ),
          })
        }
        if (directoryOpen) {
          windows.push({
            key: 'people',
            title: '🔍 Find people',
            node: (
              <PeopleDirectory
                people={people}
                seatRoomName={seatRoomName}
                roomOrder={roomOrder}
                meId={me?.id ?? null}
                focusedUserId={focusedUserId}
                onSelect={(userId) => {
                  busRef.current?.emit({ kind: 'cameraFollow', userId })
                  focusUser(userId)
                  // Same-batch raise (see onPersonFocus): without it the cap
                  // eviction sees the fresh card at default z and closes it.
                  raiseWin('person')
                  // Desktop keeps the directory open to browse several; compact
                  // swaps to the person sheet (one sheet at a time).
                  if (compactRef.current) setDirectoryOpen(false)
                }}
                onClose={() => setDirectoryOpen(false)}
                compact={compact}
              />
            ),
          })
        }
        if (compact) {
          // One sheet at a time on phones.
          const first = windows[0]
          return first ? <div key={first.key}>{first.node}</div> : null
        }
        return windows.map(({ key, title, node }) => {
          const def = WIN_DEFS[key]
          const ov = winOver[key] ?? {}
          const defX = Math.max(
            8,
            window.innerWidth - def.w - 18 - def.idx * 26,
          )
          return (
            <WindowFrame
              key={key}
              winKey={key}
              title={title}
              accent={def.accent}
              x={ov.x ?? defX}
              y={ov.y ?? 66 + def.idx * 26}
              z={ov.z ?? def.idx + 10}
              width={def.w}
              isTop={topKey === key}
              pinned={pinnedWins.includes(key)}
              collapsed={collapsedWins.includes(key)}
              onRaise={raiseWin}
              onMove={moveWin}
              onTogglePin={togglePin}
              onToggleCollapse={toggleCollapse}
            >
              {node}
            </WindowFrame>
          )
        })
      })()}

      {/* Hover peek: who/what is this, with zero windows opened. */}
      {peek &&
        !compact &&
        (() => {
          const p = people.find((x) => x.userId === peek.userId)
          if (!p) return null
          const px = Math.min(peek.x + 16, window.innerWidth - 230)
          const py = Math.min(peek.y + 12, window.innerHeight - 110)
          return (
            <div style={{ ...peekCard, left: px, top: py }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: theme.cream }}>
                {p.isAgent ? '✦ ' : ''}
                {p.name}
              </div>
              {(p.jobTitle || p.role) && (
                <div style={{ fontSize: 10.5, color: theme.textDim }}>
                  {p.jobTitle || p.role}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: presenceColor(p.presence),
                  }}
                />
                <span style={{ fontSize: 10, color: theme.textDim }}>
                  {p.presence}
                  {p.isAgent ? ' · AI agent' : ''}
                </span>
              </div>
              <div style={{ fontSize: 9.5, color: '#6a6f7d', marginTop: 4 }}>
                click to open
              </div>
            </div>
          )
        })()}

      {/* Edge event chips: the org's activity, spatially — click to jump. */}
      {!compact && Object.keys(chips).length > 0 && (
        <div style={chipStack}>
          {Object.entries(chips)
            .sort((a, b) => b[1].at - a[1].at)
            .slice(0, 5)
            .map(([pulseId, c]) => (
              <button
                key={pulseId}
                onClick={() => {
                  busRef.current?.emit({ kind: 'cameraRoom', pulseId })
                  focusRoom(pulseId)
                  setChips((prev) => {
                    const next = { ...prev }
                    delete next[pulseId]
                    return next
                  })
                }}
                style={chip}
              >
                <span style={{ fontSize: 10.5, fontWeight: 700, color: theme.cream }}>
                  {c.name.length > 16 ? `${c.name.slice(0, 15)}…` : c.name}
                  {c.count > 1 && (
                    <span style={{ color: '#ffd28a' }}> ·{c.count}</span>
                  )}
                </span>
                <span style={chipText}>{c.text}</span>
              </button>
            ))}
        </div>
      )}

      {terminalFor && (
        <AgentTerminal
          key={terminalFor.userId}
          person={terminalFor}
          organizationId={organizationId}
          getToken={getToken}
          onClose={() => setTerminalFor(null)}
        />
      )}

      {taskDetail && (
        <TaskDetailPanel
          key={taskDetail.task.id}
          task={taskDetail.task}
          pulseId={taskDetail.pulseId}
          organizationId={organizationId}
          members={people.filter((p) =>
            snap.data.pulses
              .find((x) => x.id === taskDetail.pulseId)
              ?.memberUserIds.includes(p.userId),
          )}
          getToken={getToken}
          onClose={() => setTaskDetail(null)}
        />
      )}

      {noteDetail && (
        <NoteDetailPanel
          key={noteDetail.note.id}
          note={noteDetail.note}
          pulseId={noteDetail.pulseId}
          organizationId={organizationId}
          getToken={getToken}
          onClose={() => setNoteDetail(null)}
        />
      )}

      {!(compact && (anyPanelOpen || menuOpen)) && (
        <Ticker bus={busRef.current} snapshot={snap.data} />
      )}

      {!(compact && anyPanelOpen) && (
      <>
      <div style={hint}>
        {arrangeMode
          ? 'arrange mode · drag rooms to rearrange — saved for this org'
          : liveOnly
            ? `Live only · real Zunou activity appears as it happens${
                !touch && !compact
                  ? ' · T tasks · N notes · O office · R reviews · / find people'
                  : ''
              }`
            : touch
              ? 'drag to pan · pinch to zoom · tap a character'
              : 'drag to pan · scroll to zoom · click a character · T tasks · N notes · O office · R reviews · / find people'}
      </div>

      {capToast && (
        <div style={capToastStyle} key={capToast}>
          {capToast}
        </div>
      )}

      <div style={zoomControl}>
        <button
          style={zoomBtn}
          onClick={() => busRef.current?.emit({ kind: 'camera', action: 'in' })}
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          style={zoomBtn}
          onClick={() => busRef.current?.emit({ kind: 'camera', action: 'out' })}
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          style={{ ...zoomBtn, fontSize: 15 }}
          onClick={() => busRef.current?.emit({ kind: 'camera', action: 'reset' })}
          aria-label="Fit to screen"
          title="Fit to screen"
        >
          ⤢
        </button>
      </div>
      </>
      )}
    </div>
  )
}

/** The org's heartbeat: a one-line ticker of real events as they happen —
 *  who posted where, tasks moving, agents working. Ambient events are skipped. */
export function Ticker({ bus, snapshot }: { bus: EventBus; snapshot: WorldSnapshot }) {
  // `agentId`: the line's agent wears its character (no picture) in place of ✦.
  const [line, setLine] = useState<{ text: string; at: number; agentId?: string } | null>(null)
  // A ticker line is "happening now" — expire it instead of pinning a stale
  // event next to a pulsing live dot for the rest of the session.
  useEffect(() => {
    if (!line) return
    const t = window.setTimeout(() => setLine(null), 20_000)
    return () => window.clearTimeout(t)
  }, [line])
  useEffect(() => {
    const nameOf = new Map(snapshot.people.map((p) => [p.userId, p.name.split(' ')[0]]))
    const agentSet = new Set(
      snapshot.people.filter((p) => p.isAgent).map((p) => p.userId),
    )
    // Agents with no uploaded picture show their character instead of ✦.
    const characterSet = new Set(
      snapshot.people.filter((p) => showsCharacter(p)).map((p) => p.userId),
    )
    const roomOf = new Map(snapshot.pulses.map((p) => [p.id, p.name]))
    const off = bus.on((event) => {
      let text: string | null = null
      let actor: string | null | undefined = null
      const markOf = (id: string | null | undefined): string =>
        id && agentSet.has(id) && !characterSet.has(id) ? '✦ ' : ''
      if (event.kind === 'message' && !event.ambient && event.pulseId) {
        const who = event.userId ? (nameOf.get(event.userId) ?? 'Someone') : 'Someone'
        actor = event.userId
        text = `${markOf(actor)}${who} posted in ${roomOf.get(event.pulseId) ?? 'a room'}`
      } else if (event.kind === 'task' && !event.ambient) {
        const who = event.actorId ? (nameOf.get(event.actorId) ?? 'Someone') : 'Someone'
        const room = roomOf.get(event.pulseId) ?? 'a room'
        if (event.action === 'created') text = `${who} added a task in ${room}`
        else if (event.action === 'updated') text = `${who} moved a task in ${room}`
      } else if (event.kind === 'note') {
        const who = event.actorId ? (nameOf.get(event.actorId) ?? 'Someone') : 'Someone'
        actor = event.actorId
        const mark = markOf(actor)
        const room = roomOf.get(event.pulseId) ?? 'a room'
        if (event.action === 'created') text = `${mark}${who} added a note in ${room}`
        else if (event.action === 'updated') text = `${mark}${who} edited a note in ${room}`
      } else if (event.kind === 'reaction' && !event.ambient) {
        const who = nameOf.get(event.userId) ?? 'Someone'
        text = `${who} reacted ${event.symbol}`
      } else if (event.kind === 'agentWork') {
        const who = nameOf.get(event.userId) ?? 'An agent'
        actor = event.userId
        // Standing duties (AGENT_DUTIES_SPEC.md) get their own feed voice —
        // the box stamps these summaries as 'duty fired: <slug>'.
        text = event.summary.startsWith('duty fired:')
          ? `⏰ ${who} on duty: ${event.summary.slice(11, 71).trim()}`
          : `${characterSet.has(actor) ? '' : '✦ '}${who}: ${event.summary.slice(0, 60)}`
      } else if (event.kind === 'meetingRefetch') {
        text = `meeting activity in ${roomOf.get(event.pulseId) ?? 'a room'}`
      }
      if (text) {
        setLine({
          text,
          at: Date.now(),
          agentId: actor && characterSet.has(actor) ? actor : undefined,
        })
      }
    })
    return off
  }, [bus, snapshot])

  if (!line) return null
  return (
    <div key={line.at} style={tickerWrap}>
      <span style={tickerDot} />
      {line.agentId && <AgentCharacter userId={line.agentId} size={16} />}
      <span style={tickerText}>{line.text}</span>
    </div>
  )
}

function presenceColor(presence: Person['presence']): string {
  if (presence === 'online') return theme.online
  if (presence === 'away') return theme.away
  if (presence === 'offline') return theme.offline
  return theme.textDim
}

export function DetailPanel({
  person,
  openTasks,
  rooms,
  onClose,
  compact,
  isSelf,
  destinations,
  onPing,
  onMessage,
  onTyping,
  onTerminal,
  onLocate,
  manage,
  peek,
}: {
  person: Person
  openTasks: number
  rooms: string[]
  onClose: () => void
  compact?: boolean
  isSelf?: boolean
  /** Where a ping/message can go — the clicked pulse, the 1:1, shared teams. */
  destinations?: MessageDestination[]
  onPing?: (
    dest: MessageDestination,
    emoji: string,
  ) => Promise<{ mode: 'reacted' | 'messaged'; pulseName: string }>
  onMessage?: (dest: MessageDestination, text: string) => Promise<void>
  /** Fire "…is typing" for the selected destination (null-pulse DMs skip it). */
  onTyping?: (dest: MessageDestination, isTyping: boolean) => void
  /** Opens the agent's live terminal (work-log tail). Agents + org owners only. */
  onTerminal?: () => void
  /** Pan the camera to this person and soft-follow them. */
  onLocate?: () => void
  /** Extra management UI (e.g. agent controls) rendered below the actions. */
  manage?: React.ReactNode
  /** The deep peek: real workload + (for agents) live activity. */
  peek?: React.ReactNode
}) {
  const avatar = person.picture || person.gravatar || ''
  const character = showsCharacter(person)
  const [composerOpen, setComposerOpen] = useState(false)
  const [dmDraft, setDmDraft] = useState('')
  const [dmState, setDmState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [dmError, setDmError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [destIdx, setDestIdx] = useState(0)
  const dests = destinations ?? []
  const dest = dests[Math.min(destIdx, Math.max(0, dests.length - 1))]
  const firstName = person.name.split(' ')[0]

  const deliver = async (text: string) => {
    if (!onMessage || !dest || !text.trim()) return
    setDmState('sending')
    setDmError(null)
    try {
      if (dest.pulseId) onTyping?.(dest, false)
      await onMessage(dest, text.trim())
      setDmState('sent')
      setDmDraft('')
      setComposerOpen(false)
      setFeedback(`Sent to ${dest.here ? dest.label : dest.label} ✓`)
      setTimeout(() => {
        setDmState('idle')
        setFeedback(null)
      }, 2500)
    } catch (e) {
      setDmState('error')
      setDmError(e instanceof Error ? e.message.slice(0, 120) : 'Send failed')
    }
  }

  const ping = async () => {
    if (!onPing || !dest) return
    setDmState('sending')
    setDmError(null)
    try {
      const r = await onPing(dest, '👋')
      setDmState('sent')
      setFeedback(
        r.mode === 'reacted'
          ? `Waved at ${firstName}'s last message in ${r.pulseName} ✓`
          : `Waved to ${r.pulseName} ✓`,
      )
      setTimeout(() => {
        setDmState('idle')
        setFeedback(null)
      }, 2800)
    } catch (e) {
      setDmState('error')
      setDmError(e instanceof Error ? e.message.slice(0, 120) : 'Ping failed')
    }
  }
  return (
    <aside style={compact ? panelCompact : panel}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {onLocate && (
          <button
            onClick={onLocate}
            title="Find them on the floor (camera follows)"
            style={{ ...linkBtn, fontSize: 13 }}
          >
            📍
          </button>
        )}
        <button onClick={onClose} style={{ ...linkBtn, fontSize: 18 }}>×</button>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: -8 }}>
        <div style={avatarWrap}>
          {character ? (
            // Its character, still, with its role's emblem in the corner (where
            // the presence dot would sit: an agent here is always on).
            <AgentCharacter userId={person.userId} size={46} />
          ) : (
            <>
              {avatar ? (
                <img src={avatar} alt="" style={avatarImg} referrerPolicy="no-referrer" />
              ) : (
                <span style={avatarFallback}>{person.name.slice(0, 1).toUpperCase()}</span>
              )}
              <span
                style={{
                  position: 'absolute',
                  right: -1,
                  bottom: -1,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: presenceColor(person.presence),
                  border: `2px solid ${theme.panel}`,
                }}
              />
            </>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: theme.cream,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {person.name}
          </div>
          {/* The "AI agent" label only beside a picture: the character says it. */}
          {!character && (
            <span
              style={{
                display: 'inline-block',
                marginTop: 3,
                fontSize: 10.5,
                padding: '2px 8px',
                borderRadius: 6,
                color: '#15161d',
                background: person.isAgent ? theme.agent : theme.human,
              }}
            >
              {person.isAgent ? 'AI agent' : 'human'}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <MiniStat value={openTasks} label="open tasks" />
        <MiniStat value={rooms.length} label="rooms" />
      </div>

      {!isSelf && onMessage && dest && (
        <div style={{ marginTop: 12 }}>
          {/* Where it lands: defaults to the pulse you clicked them in, plus
              the 1:1 and any shared team chats. */}
          {dests.length > 1 && (
            <label style={{ display: 'block', marginBottom: 7 }}>
              <span style={{ fontSize: 10, color: theme.textDim }}>Send to</span>
              <select
                value={destIdx}
                onChange={(e) => setDestIdx(Number(e.target.value))}
                style={destSelect}
              >
                {dests.map((d, i) => (
                  <option key={d.key} value={i}>
                    {d.label}
                    {d.here ? ' · here' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => void ping()}
              disabled={dmState === 'sending'}
              style={actionBtn}
              title={`Wave at ${firstName} in ${dest.label}`}
            >
              👋 Ping
            </button>
            <button
              onClick={() => setComposerOpen((v) => !v)}
              style={composerOpen ? actionBtnActive : actionBtn}
            >
              💬 Message
            </button>
            {onTerminal && (
              <button
                onClick={onTerminal}
                style={{ ...actionBtn, flex: '0 0 auto' }}
                title="Live work-log terminal"
              >
                ▮▶
              </button>
            )}
          </div>
          {composerOpen && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void deliver(dmDraft)
              }}
              style={{ display: 'flex', gap: 6, marginTop: 8 }}
            >
              <input
                value={dmDraft}
                onChange={(e) => {
                  setDmDraft(e.target.value)
                  if (dest.pulseId) onTyping?.(dest, e.target.value.trim().length > 0)
                }}
                onBlur={() => dest.pulseId && onTyping?.(dest, false)}
                placeholder={
                  dest.dm
                    ? `Message ${firstName}…`
                    : `Message @${firstName} in ${dest.label}…`
                }
                autoFocus
                maxLength={4000}
                style={dmInput}
              />
              <button
                type="submit"
                disabled={dmState === 'sending' || !dmDraft.trim()}
                style={dmSend}
              >
                {dmState === 'sending' ? '…' : 'Send'}
              </button>
            </form>
          )}
          {feedback && (
            <div style={{ fontSize: 11, color: theme.online, marginTop: 5 }}>{feedback}</div>
          )}
          {dmState === 'error' && (
            <div style={{ fontSize: 11, color: '#ff8a7a', marginTop: 5 }}>
              {dmError ?? 'Send failed'} — try again
            </div>
          )}
        </div>
      )}

      {peek}
      {manage}

      {person.jobTitle && <Row label="Role" value={person.jobTitle} />}
      {person.department && <Row label="Department" value={person.department} />}
      <Row label="Presence" value={person.presence} />
      {person.role && <Row label="Org role" value={person.role} />}

      {rooms.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: theme.textDim, marginBottom: 5 }}>
            Member of
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {rooms.slice(0, 8).map((r) => (
              <span key={r} style={roomChip}>
                {r}
              </span>
            ))}
          </div>
        </div>
      )}
    </aside>
  )
}

function MiniStat({ value, label }: { value: number; label: string }) {
  return (
    <div
      style={{
        flex: 1,
        padding: '8px 10px',
        background: '#0e0f15',
        border: `1px solid ${theme.border}`,
        borderRadius: 9,
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 700, color: theme.cream }}>{value}</div>
      <div style={{ fontSize: 10.5, color: theme.textDim }}>{label}</div>
    </div>
  )
}

function Stat({
  color,
  value,
  label,
  dot,
  compact,
}: {
  color: string
  value: number
  label: string
  dot?: boolean
  compact?: boolean
}) {
  return (
    <span style={{ ...statPill, padding: compact ? '7px 11px' : '5px 11px', fontSize: compact ? 13 : 12.5 }}>
      {dot && (
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
      )}
      <b style={{ color, fontWeight: 600 }}>{value}</b>
      {!compact && <span style={{ color: theme.textDim }}>{label}</span>}
    </span>
  )
}

/** True on narrow viewports (phones / small tablets). */
function useCompact(breakpoint = 640): boolean {
  const [compact, setCompact] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  )
  useEffect(() => {
    const onResize = () => setCompact(window.innerWidth < breakpoint)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [breakpoint])
  return compact
}

/** True on touch devices, so hints + gestures can reflect pinch/tap. */
function useTouch(): boolean {
  const [touch, setTouch] = useState(false)
  useEffect(() => {
    setTouch(
      typeof window !== 'undefined' &&
        ('ontouchstart' in window || navigator.maxTouchPoints > 0),
    )
  }, [])
  return touch
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13 }}>
      <span style={{ color: theme.textDim }}>{label}</span>
      <span style={{ color: theme.text }}>{value}</span>
    </div>
  )
}

const hudBar: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 20px',
  // A real bar (not a fading gradient): controls read as one tidy nav and
  // never visually bleed into the world beneath.
  background: '#0e0f14e8',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderBottom: '1px solid #1d2029',
  pointerEvents: 'none',
}
const navDivider: React.CSSProperties = {
  width: 1,
  height: 18,
  background: '#262a36',
  margin: '0 3px',
  flex: '0 0 auto',
}
const arrangeBar: React.CSSProperties = {
  position: 'absolute',
  top: 60,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 25,
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  padding: '8px 12px',
  background: '#14161ef2',
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  boxShadow: '0 12px 34px #000a',
  pointerEvents: 'auto',
  whiteSpace: 'nowrap',
}

const panel: React.CSSProperties = {
  position: 'absolute',
  top: 70,
  right: 18,
  width: 272,
  maxHeight: 'calc(100% - 130px)',
  overflowY: 'auto',
  padding: 16,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  color: theme.text,
}
// On phones the detail card becomes a full-width bottom sheet so it never
// collides with the (possibly two-row) HUD or run off the edge.
const panelCompact: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: '72vh',
  overflowY: 'auto',
  padding: 16,
  paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
  background: theme.panel,
  borderTop: `1px solid ${theme.border}`,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  color: theme.text,
}

const avatarWrap: React.CSSProperties = {
  position: 'relative',
  width: 46,
  height: 46,
  flex: '0 0 auto',
}
const avatarImg: React.CSSProperties = {
  width: 46,
  height: 46,
  borderRadius: 12,
  objectFit: 'cover',
  border: `1px solid ${theme.border}`,
}
const avatarFallback: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 46,
  height: 46,
  borderRadius: 12,
  background: theme.terracotta,
  color: '#1a120e',
  fontSize: 20,
  fontWeight: 700,
  border: `1px solid ${theme.border}`,
}
const roomChip: React.CSSProperties = {
  fontSize: 10.5,
  color: theme.text,
  background: '#0e0f15',
  border: `1px solid ${theme.border}`,
  borderRadius: 6,
  padding: '2px 7px',
}
const capToastStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 34,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1200,
  padding: '7px 14px',
  background: '#1d1712f0',
  border: '1px solid #4a3d1e',
  borderRadius: 9,
  color: '#ffd28a',
  fontSize: 11.5,
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
}
const hint: React.CSSProperties = {
  position: 'absolute',
  bottom: 12,
  left: 0,
  right: 0,
  textAlign: 'center',
  fontSize: 12,
  color: theme.offline,
  pointerEvents: 'none',
}
const zoomControl: React.CSSProperties = {
  position: 'absolute',
  right: 16,
  bottom: 40,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  pointerEvents: 'auto',
}
const peekCard: React.CSSProperties = {
  position: 'absolute',
  zIndex: 900,
  width: 210,
  padding: '8px 10px',
  background: '#171a21f2',
  border: `1px solid ${theme.border}`,
  borderRadius: 9,
  boxShadow: '0 10px 30px #000a',
  pointerEvents: 'none',
}
const chipStack: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  top: 90,
  zIndex: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  pointerEvents: 'auto',
}
const chip: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1,
  maxWidth: 190,
  padding: '6px 10px',
  background: '#171a21ee',
  border: '1px solid #3a3320',
  borderLeft: '3px solid #ffd28a',
  borderRadius: 9,
  cursor: 'pointer',
  textAlign: 'left',
}
const chipText: React.CSSProperties = {
  fontSize: 10,
  color: theme.textDim,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 170,
}
const menuBackdrop: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 1100,
  background: '#00000055',
}
const flyMenu: React.CSSProperties = {
  position: 'absolute',
  top: 62,
  right: 10,
  zIndex: 1101,
  width: 210,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 10,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 12,
  boxShadow: '0 16px 44px #000b',
}
const menuItem: React.CSSProperties = {
  display: 'block',
  width: '100%',
  background: '#101218',
  border: `1px solid ${theme.border}`,
  color: theme.text,
  borderRadius: 9,
  fontSize: 13,
  padding: '10px 12px',
  cursor: 'pointer',
  textAlign: 'left',
}
const zoomBtn: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 10,
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  color: theme.cream,
  fontSize: 22,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
}

const tickerWrap: React.CSSProperties = {
  position: 'absolute',
  bottom: 34,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  maxWidth: 'min(560px, 86vw)',
  padding: '5px 12px',
  background: '#15161dd9',
  border: `1px solid ${theme.border}`,
  borderRadius: 999,
  pointerEvents: 'none',
  animation: 'go-ticker-in 240ms ease-out',
}
const tickerDot: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: theme.online,
  flex: '0 0 auto',
  animation: 'go-live-pulse 1.6s ease-in-out infinite',
}
const tickerText: React.CSSProperties = {
  fontSize: 11.5,
  color: theme.cream,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const actionBtn: React.CSSProperties = {
  flex: 1,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  color: theme.cream,
  borderRadius: 9,
  fontSize: 12.5,
  fontWeight: 600,
  padding: '8px 10px',
  cursor: 'pointer',
}
const actionBtnActive: React.CSSProperties = {
  ...actionBtn,
  borderColor: '#3d251c',
  background: '#241a15',
  color: theme.terracottaLite,
}
const dmInput: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  fontSize: 12.5,
  padding: '8px 10px',
  outline: 'none',
}
const destSelect: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 3,
  background: '#101218',
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  fontSize: 12,
  padding: '6px 8px',
  outline: 'none',
}
const dmSend: React.CSSProperties = {
  background: theme.terracotta,
  color: '#1a120e',
  border: 'none',
  borderRadius: 8,
  fontSize: 12.5,
  fontWeight: 700,
  padding: '8px 12px',
  cursor: 'pointer',
}

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 13,
  pointerEvents: 'auto',
}

const logoMark: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  background: theme.terracotta,
  color: '#1a120e',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 17,
  fontFamily: 'monospace',
  flex: '0 0 auto',
}
const subline: React.CSSProperties = {
  fontSize: 10.5,
  color: theme.textDim,
  letterSpacing: 1,
  textTransform: 'uppercase',
  marginTop: 1,
}
const segWrap: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  padding: 2,
  background: '#0b0c11',
  border: `1px solid ${theme.border}`,
  borderRadius: 20,
  pointerEvents: 'auto',
}
const segBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.5,
  padding: '3px 10px',
  borderRadius: 16,
  border: '1px solid transparent',
  background: 'none',
  color: theme.textDim,
  cursor: 'pointer',
}
const segIdle = segBase
const segActiveShowcase: React.CSSProperties = {
  ...segBase,
  color: theme.terracotta,
  background: '#241712',
  border: '1px solid #3d251c',
}
const segActiveLive: React.CSSProperties = {
  ...segBase,
  color: theme.online,
  background: '#10221a',
  border: '1px solid #1d3a2c',
}
const segDotIdle: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: theme.border,
}
// Larger touch targets for the mode switch on phones.
const segCompact: React.CSSProperties = {
  padding: '9px 14px',
  fontSize: 12,
  letterSpacing: 0.4,
}
const liveDot: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: theme.online,
  animation: 'go-live-pulse 1.4s ease-in-out infinite',
}
const showcaseDot: React.CSSProperties = {
  ...liveDot,
  background: theme.terracotta,
}
const statPill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12.5,
  padding: '5px 11px',
  background: theme.panel,
  border: `1px solid ${theme.border}`,
  borderRadius: 20,
  pointerEvents: 'auto',
}
const ghostBtn: React.CSSProperties = {
  background: 'none',
  border: `1px solid ${theme.border}`,
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 12,
  padding: '6px 11px',
  borderRadius: 8,
  pointerEvents: 'auto',
}
